import { Container, Sprite, Texture } from 'pixi.js';
import type { LayerStackAsset } from './LayerStackLoader';
import {
  drawList,
  mirrorAnchorX,
  pulseFrames,
  stateNames,
  type DrawEntry,
  type Facing,
  type LayerStackManifest,
  type PulseFrame,
  type StateName,
} from './ManifestTypes';

export const PULSE_FPS = 8;
const PULSE_FRAME_MS = 1000 / PULSE_FPS;

/** Called after the new state's draw list is applied, so listeners observe the settled sprite. */
export type StateChangeListener = (next: StateName, prev: StateName) => void;

/** Called once per `advance` that moved the cycle, with the frame now on screen. */
export type PulseFrameListener = (frameIndex: number) => void;

export interface AnchorPoint {
  /** Anchor in the sprite's own `target` space, already mirrored when mirrored. */
  readonly localX: number;
  readonly localY: number;
  readonly worldX: number;
  readonly worldY: number;
}

/**
 * getAnchor()'s return value, reused across every call on every sprite. Every
 * caller (player.ts's muzzle flash, fx/Projectiles.ts, fx/FxDirector.ts) reads
 * the fields synchronously right after the call and never holds the reference
 * across a frame, so one shared mutable object is safe and turns a per-call
 * allocation — significant while player.ts calls this every synced frame the
 * ship is firing — into zero.
 */
const anchorResult: { localX: number; localY: number; worldX: number; worldY: number } = {
  localX: 0,
  localY: 0,
  worldX: 0,
  worldY: 0,
};

/**
 * A schema-v2 layer stack as a Pixi container.
 *
 * One child sprite per draw-list slot, painted in list order — the lists are
 * complete and already z-sorted, so array order is paint order and no
 * join-by-name is needed. State changes swap the whole list (hidden layers are
 * absent from it, remapped layers point at their own raster). Mirroring flips
 * an inner container about the target width, which is exactly the manifest's
 * `mirrorRule.anchors` rule, so anchors mirror with the art for free.
 */
export class LayerStackSprite extends Container {
  readonly manifest: LayerStackManifest;
  readonly targetWidth: number;
  readonly targetHeight: number;
  private readonly textures: ReadonlyMap<string, Texture>;
  private readonly stack = new Container();
  private readonly layerSprites: Sprite[] = [];
  private readonly scratch = { x: 0, y: 0 };
  private readonly stateListeners = new Set<StateChangeListener>();
  private readonly pulseListeners = new Set<PulseFrameListener>();
  private currentState: StateName = null;
  private mirrored = false;
  private pulsing = false;
  private frameIndex = 0;
  private frameClockMs = 0;
  private readonly pulsePhaseMs: number;
  private tintValue = 0xffffff;
  private hiddenLayerNames: ReadonlySet<string> = new Set();

  constructor(asset: LayerStackAsset, pulsePhaseMs = 0) {
    super();
    this.manifest = asset.manifest;
    this.textures = asset.textures;
    this.targetWidth = asset.manifest.target[0];
    this.targetHeight = asset.manifest.target[1];
    this.pulsePhaseMs = Math.max(0, Math.min(PULSE_FRAME_MS - 1, pulsePhaseMs));
    this.label = `${asset.manifest.slug}:${asset.manifest.rung}`;
    this.addChild(this.stack);
    this.applyDrawList(drawList(this.manifest, null, null));
  }

  get facing(): Facing {
    return this.manifest.facing;
  }

  get stateName(): StateName {
    return this.currentState;
  }

  get states(): string[] {
    return stateNames(this.manifest);
  }

  get mirror(): boolean {
    return this.mirrored;
  }

  get playing(): boolean {
    return this.pulsing;
  }

  get pulseFrame(): number {
    return this.frameIndex;
  }

  /** null when this state has no cycle to play: `pulse: null`, or an empty master cycle. */
  get pulse(): readonly PulseFrame[] | null {
    return pulseFrames(this.manifest, this.currentState);
  }

  /** Master stack for null, otherwise that state's stack. Resets the cycle to frame 0. */
  setState(state: StateName): void {
    if (this.currentState === state) return;
    const previous = this.currentState;
    this.currentState = state;
    this.frameIndex = 0;
    this.frameClockMs = this.pulsePhaseMs;
    this.refresh();
    for (const listener of this.stateListeners) listener(state, previous);
  }

  /**
   * Observes state entry. Returns the unsubscribe — callers that outlive the sprite
   * (audio bindings, telemetry) must call it, nothing here holds a weak reference.
   */
  onStateChange(fn: StateChangeListener): () => void {
    this.stateListeners.add(fn);
    return () => {
      this.stateListeners.delete(fn);
    };
  }

  /**
   * Observes the pulse cycle. Only frame advances fire — resetting the cycle via
   * `setState` / `setPulsing` is not a frame event and stays silent.
   */
  onPulseFrame(fn: PulseFrameListener): () => void {
    this.pulseListeners.add(fn);
    return () => {
      this.pulseListeners.delete(fn);
    };
  }

  /**
   * Records the caller's intent even for a state that cannot pulse, so stepping
   * through a `pulse: null` state and back resumes playback without being re-armed.
   */
  setPulsing(playing: boolean): void {
    if (this.pulsing === playing) return;
    this.pulsing = playing;
    this.frameIndex = 0;
    this.frameClockMs = this.pulsePhaseMs;
    this.refresh();
  }

  /** Advances the ~8fps cycle. A no-op while paused or when the state has no cycle. */
  advance(deltaMs: number): void {
    if (!this.pulsing) return;
    const frames = this.pulse;
    if (frames === null) return;
    this.frameClockMs += deltaMs;
    if (this.frameClockMs < PULSE_FRAME_MS) return;
    const steps = Math.floor(this.frameClockMs / PULSE_FRAME_MS);
    this.frameClockMs -= steps * PULSE_FRAME_MS;
    this.frameIndex = (this.frameIndex + steps) % frames.length;
    this.refresh();
    for (const listener of this.pulseListeners) listener(this.frameIndex);
  }

  /** manifest.mirrorRule: flip the raster horizontally; anchors follow as x -> width - x. */
  setMirror(mirrored: boolean): void {
    if (this.mirrored === mirrored) return;
    this.mirrored = mirrored;
    this.stack.scale.x = mirrored ? -1 : 1;
    this.stack.x = mirrored ? this.targetWidth : 0;
  }

  /** Mirrors only when the authored master faces the other way. */
  setFacing(facing: Facing): void {
    this.setMirror(facing !== this.manifest.facing);
  }

  /**
   * Renderer-local kind differentiation for a slug whose authored asset has
   * no per-kind state to swap to (e.g. the shared pickup glyph, which only
   * ships `neutral`). This is distinct from StateFlash's "no tint hack": that
   * note is about damage/fire reads that already have authored states to
   * swap between — a plain color multiply is the only option when no state
   * exists at all.
   */
  setTint(color: number): void {
    if (this.tintValue === color) return;
    this.tintValue = color;
    for (const sprite of this.layerSprites) sprite.tint = color;
  }

  /**
   * Drops named layers from every draw list this sprite ever paints (master,
   * states, pulse frames), by name — the same join key `drawList` already
   * uses to pick entries. For R2 background bands this hides the opaque
   * sky-fill layer so nearer bands stop stacking into a horizontal seam; the
   * far band paints the only sky. Re-applies the current draw list immediately
   * so a call after construction still takes effect.
   */
  setHiddenLayerNames(names: readonly string[]): void {
    this.hiddenLayerNames = new Set(names);
    this.refresh();
  }

  /** Pixel art moves on whole pixels; the display scale is applied by the renderer. */
  setPixelPosition(x: number, y: number): void {
    this.x = Math.round(x);
    this.y = Math.round(y);
  }

  /**
   * Returns a shared mutable object, not a fresh one — see `anchorResult`'s own
   * comment. Read it immediately; a second getAnchor() call anywhere overwrites it.
   */
  getAnchor(name: string): AnchorPoint {
    const anchor = this.manifest.anchors[name];
    if (!anchor) throw new Error(`${this.label}: unknown anchor "${name}"`);
    const localX = this.mirrored ? mirrorAnchorX(this.manifest, anchor[0]) : anchor[0];
    const localY = anchor[1];
    this.scratch.x = localX;
    this.scratch.y = localY;
    const world = this.toGlobal(this.scratch, this.scratch) as { x: number; y: number };
    anchorResult.localX = localX;
    anchorResult.localY = localY;
    anchorResult.worldX = world.x;
    anchorResult.worldY = world.y;
    return anchorResult;
  }

  private refresh(): void {
    const frame = this.pulsing && this.pulse !== null ? this.frameIndex : null;
    this.applyDrawList(drawList(this.manifest, this.currentState, frame));
  }

  private applyDrawList(entries: readonly DrawEntry[]): void {
    let visibleIndex = 0;
    for (let entryIndex = 0; entryIndex < entries.length; entryIndex += 1) {
      const entry = entries[entryIndex]!;
      if (this.hiddenLayerNames.has(entry.name)) continue;
      let sprite = this.layerSprites[visibleIndex];
      if (!sprite) {
        sprite = new Sprite();
        sprite.roundPixels = true;
        sprite.tint = this.tintValue;
        this.layerSprites.push(sprite);
        this.stack.addChild(sprite);
      }
      const texture = this.textures.get(entry.file);
      if (!texture) throw new Error(`${this.label}: raster not loaded for ${entry.file}`);
      if (sprite.texture !== texture) sprite.texture = texture;
      if (sprite.x !== entry.offset[0] || sprite.y !== entry.offset[1]) {
        sprite.position.set(entry.offset[0], entry.offset[1]);
      }
      if (!sprite.visible) sprite.visible = true;
      visibleIndex += 1;
    }
    for (let i = visibleIndex; i < this.layerSprites.length; i += 1) {
      if (this.layerSprites[i]!.visible) this.layerSprites[i]!.visible = false;
    }
  }
}
